import React, { useEffect, useState } from 'react';
import { Avatar, Button, HStack, Link, Menu, MenuButton, MenuDivider, MenuGroup, MenuItem, MenuList, Spacer, Text, VStack } from '@chakra-ui/react';
import { ChevronDownIcon } from '@chakra-ui/icons';
import { VscPlay } from 'react-icons/vsc';
import Const from '../Constants';
import { useAgentsStore } from '../Stores/AgentsStore';
import { useNavigationStore } from '../Stores/NavigationStore';
import { getAvatarUrl } from '../Utils';

const AgentTaskRunner = ({ auth, task, onAssign, setAppMode }) => {
    const runAgent = useAgentsStore((s) => s.runAgent);
    const runningTaskIds = useAgentsStore((s) => s.runningTaskIds);
    const globalAgents = useAgentsStore((s) => s.globalAgents);
    const localAgents = useAgentsStore((s) => s.localAgents);
    const setTargetNoteId = useNavigationStore((s) => s.setTargetNoteId);

    const allAgents = [...globalAgents, ...localAgents];

    const [selectedAgentId, setSelectedAgentId] = useState(task?.assigneeId || '');
    const [lastNoteId, setLastNoteId] = useState(null);

    useEffect(() => {
        setSelectedAgentId(task?.assigneeId || '');
    }, [task]);

    const isRunning = runningTaskIds.includes(task?.id);
    const canRun = !!selectedAgentId && !isRunning;

    return (
        <VStack align='stretch' spacing={2}>
            <HStack>
                <Text
                    fontSize={Const.SM}
                    fontWeight={Const.Bold}
                    align={Const.Left}
                    color={Const.Gray165}
                >
                    Assigned agent
                </Text>
                <Spacer />
                <Menu>
                    <MenuButton
                        as={Button}
                        rightIcon={<ChevronDownIcon />}
                        size={Const.SM}
                        width='40'
                        bg={Const.Gray36}
                        color={Const.White}
                        border='1px solid'
                        borderColor={Const.Gray64}
                        borderRadius='md'
                        fontSize={Const.SM}
                        textAlign='left'
                        _hover={{ borderColor: Const.Gray100, bg: Const.Gray36 }}
                        _focus={{ boxShadow: 'none' }}
                        _expanded={{ bg: Const.Gray36 }}
                    >
                        {selectedAgentId
                            ? (() => {
                                const agent = allAgents.find(a => a.id === selectedAgentId);
                                return agent ? (
                                    <HStack spacing={2}>
                                        <Avatar src={getAvatarUrl(agent.avatar)} name={agent.name} size='xs' borderWidth={1} />
                                        <Text fontSize={Const.SM}>{agent.name}</Text>
                                    </HStack>
                                ) : 'None';
                            })()
                            : 'None'
                        }
                    </MenuButton>
                    <MenuList bg={Const.Gray36} borderColor={Const.Gray64} borderRadius='md' minW='0' width='60'>
                        <MenuItem
                            bg={Const.Gray36}
                            color={Const.White}
                            fontSize={Const.SM}
                            _hover={{ bg: Const.Gray64 }}
                            onClick={() => { setSelectedAgentId(''); onAssign(null); }}
                        >
                            None
                        </MenuItem>
                        {globalAgents.length > 0 && (
                            <>
                                <MenuDivider borderColor={Const.Gray64} />
                                <MenuGroup title='System Agents' color={Const.Gray165} fontSize='xs'>
                                    {globalAgents.map((agent) => (
                                        <MenuItem
                                            key={agent.id}
                                            bg={Const.Gray36}
                                            color={Const.White}
                                            fontSize={Const.SM}
                                            _hover={{ bg: Const.Gray64 }}
                                            onClick={() => { setSelectedAgentId(agent.id); onAssign(agent.id); }}
                                        >
                                            <HStack spacing={2} align='center'>
                                                <Avatar src={getAvatarUrl(agent.avatar)} name={agent.name} size='xs' borderWidth={1} />
                                                <VStack spacing={0} align='start'>
                                                    <Text fontSize={Const.SM}>{agent.name}</Text>
                                                    {agent.role ? <Text fontSize='xs' color={Const.Gray165}>{agent.role}</Text> : null}
                                                </VStack>
                                            </HStack>
                                        </MenuItem>
                                    ))}
                                </MenuGroup>
                            </>
                        )}
                        {localAgents.length > 0 && (
                            <>
                                <MenuDivider borderColor={Const.Gray64} />
                                <MenuGroup title='My Agents' color={Const.Gray165} fontSize='xs'>
                                    {localAgents.map((agent) => (
                                        <MenuItem
                                            key={agent.id}
                                            bg={Const.Gray36}
                                            color={Const.White}
                                            fontSize={Const.SM}
                                            _hover={{ bg: Const.Gray64 }}
                                            onClick={() => { setSelectedAgentId(agent.id); onAssign(agent.id); }}
                                        >
                                            <HStack spacing={2} align='center'>
                                                <Avatar src={getAvatarUrl(agent.avatar)} name={agent.name} size='xs' borderWidth={1} />
                                                <VStack spacing={0} align='start'>
                                                    <Text fontSize={Const.SM}>{agent.name}</Text>
                                                    {agent.role ? <Text fontSize='xs' color={Const.Gray165}>{agent.role}</Text> : null}
                                                </VStack>
                                            </HStack>
                                        </MenuItem>
                                    ))}
                                </MenuGroup>
                            </>
                        )}
                    </MenuList>
                </Menu>
            </HStack>
            <Button
                leftIcon={<VscPlay />}
                size='sm'
                colorScheme='orange'
                variant='solid'
                isDisabled={!canRun}
                isLoading={isRunning}
                onClick={async () => {
                    setLastNoteId(null);
                    const result = await runAgent(auth, task.id, selectedAgentId);
                    if (result?.noteId) setLastNoteId(result.noteId);
                }}
                width='100%'
            >
                Agent Run
            </Button>
            {lastNoteId && (
                <Text fontSize='xs' color={Const.Gray165} textAlign='center'>
                    Run result saved to{' '}
                    <Link
                        color={Const.Orange400}
                        textDecoration='underline'
                        onClick={() => {
                            setTargetNoteId(lastNoteId);
                            setAppMode?.(Const.sNotes);
                        }}
                    >
                        Agents Work
                    </Link>
                    {' '}in notes.
                </Text>
            )}
        </VStack>
    );
};

export default AgentTaskRunner;
